/**
 * Simple logging utility that only logs in development mode
 *
 * Usage:
 *   import { logger } from '@/utils/debug';
 *
 *   logger.log('Component render', { data });
 *   logger.warn('Deprecated API used');
 *   logger.error('Failed to load data', error);
 *   logger.info('User action', { action: 'click' });
 *   logger.debug('Detailed debug info', { state });
 */

const isDevelopment = import.meta.env.DEV;
function createLogger(
  method: "log" | "warn" | "error" | "info" | "debug"
): (...args: any[]) => void {
  if (!isDevelopment) {
    return () => {};
  }

  return (...args: any[]) => {
    console[method](...args);
  };
}

export const logger = {
  log: createLogger("log"),

  warn: createLogger("warn"),

  error: createLogger("error"),

  info: createLogger("info"),

  debug: createLogger("debug"),

  group: isDevelopment ? (label: string) => console.group(label) : () => {},

  groupEnd: isDevelopment ? () => console.groupEnd() : () => {},

  groupCollapsed: isDevelopment
    ? (label: string) => console.groupCollapsed(label)
    : () => {},

  time: isDevelopment ? (label: string) => console.time(label) : () => {},

  timeEnd: isDevelopment ? (label: string) => console.timeEnd(label) : () => {},
};

export const isLoggingEnabled = isDevelopment;
